import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  CircularProgress
} from '@mui/material';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ContentPreviewPanel, ContentItem } from '../wizard-panels/ContentPreviewPanel';
import { ValidationResultsPanel, ValidationResult } from '../wizard-panels/ValidationResultsPanel';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { moodleApi, MoodleSource, MoodleCourse } from '../../services/moodle-api';

export interface MoodleToMoodleMigrationWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

const WIZARD_STEPS = [
  { label: 'Select Source', description: 'Choose source Moodle instance' },
  { label: 'Select Content', description: 'Choose courses to migrate' },
  { label: 'Select Destination', description: 'Choose destination Moodle instance' },
  { label: 'Configure Migration', description: 'Set migration options' },
  { label: 'Review & Confirm', description: 'Review and start migration' }
];

// Step 1: Select Source Moodle Instance
const SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSources = async () => {
      try {
        const sourcesData = await moodleApi.listSources();
        setSources(sourcesData);
      } catch (err: any) {
        wizard.setError(`Failed to load Moodle sources: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    const selectedSourceId = wizard.getData('sourceId');
    const isValid = !!selectedSourceId && sources.some(s => s.id === selectedSourceId);
    wizard.setStepValidation(0, isValid);
  }, [wizard.data, sources]);

  const handleSourceChange = (sourceId: string) => {
    wizard.setData('sourceId', sourceId);
    const source = sources.find(s => s.id === sourceId);
    wizard.setData('sourceName', source?.name);
    // Clear downstream selections
    wizard.setData('selectedCourses', []);
    wizard.setData('courses', []);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>Source Moodle Instance</InputLabel>
        <Select
          value={wizard.getData('sourceId', '')}
          onChange={(e) => handleSourceChange(e.target.value)}
          label="Source Moodle Instance"
        >
          {sources.map(source => (
            <MenuItem key={source.id} value={source.id}>
              <Box>
                <Typography variant="body1">{source.name}</Typography>
                {source.description && (
                  <Typography variant="caption" color="text.secondary">
                    {source.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sources.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No Moodle instances configured. Please add a Moodle instance in the Config section first.
        </Alert>
      )}

      {wizard.getData('sourceId') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Selected source: <strong>{wizard.getData('sourceName')}</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select Content
const ContentSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>(
    wizard.getData('selectedCourseIds', [])
  );
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadCourses = async () => {
      const sourceId = wizard.getData('sourceId');
      if (!sourceId) return;

      setLoading(true);
      try {
        const coursesData = await moodleApi.listCourses(sourceId);
        setCourses(coursesData);
        wizard.setData('courses', coursesData);
      } catch (err: any) {
        wizard.setError(`Failed to load courses: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  }, []);

  useEffect(() => {
    wizard.setData('selectedCourseIds', selectedCourseIds);
    wizard.setData('selectedCourses', courses.filter(c => selectedCourseIds.includes(c.id)));
    wizard.setStepValidation(1, selectedCourseIds.length > 0);
  }, [selectedCourseIds, courses]);

  const toggleCourse = (courseId: number) => {
    setSelectedCourseIds(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleAll = () => {
    if (selectedCourseIds.length === filteredCourses.length) {
      setSelectedCourseIds([]);
    } else {
      setSelectedCourseIds(filteredCourses.map(c => c.id));
    }
  };

  const filteredCourses = courses.filter(course =>
    searchTerm === '' ||
    course.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.shortname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        label="Search courses"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search by name or short name..."
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedCourseIds.length === filteredCourses.length && filteredCourses.length > 0}
              indeterminate={selectedCourseIds.length > 0 && selectedCourseIds.length < filteredCourses.length}
              onChange={toggleAll}
            />
          }
          label="Select All"
        />
        <Typography variant="body2" color="text.secondary">
          {selectedCourseIds.length} of {courses.length} courses selected
        </Typography>
      </Box>

      {filteredCourses.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'No courses match your search.' : 'No courses found in this Moodle instance.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" width={50}></TableCell>
                <TableCell>Course Name</TableCell>
                <TableCell>Short Name</TableCell>
                <TableCell>Visible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCourses.map((course) => (
                <TableRow
                  key={course.id}
                  hover
                  onClick={() => toggleCourse(course.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={selectedCourseIds.includes(course.id)} />
                  </TableCell>
                  <TableCell>{course.fullname}</TableCell>
                  <TableCell>
                    <Chip label={course.shortname} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={course.visible ? 'Visible' : 'Hidden'}
                      size="small"
                      color={course.visible ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// Step 3: Select Destination
const DestinationSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSources = async () => {
      try {
        const sourcesData = await moodleApi.listSources();
        // Exclude the source instance
        const sourceId = wizard.getData('sourceId');
        setSources(sourcesData.filter(s => s.id !== sourceId));
      } catch (err: any) {
        wizard.setError(`Failed to load Moodle instances: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    const destinationId = wizard.getData('destinationId');
    const isValid = !!destinationId && sources.some(s => s.id === destinationId);
    wizard.setStepValidation(2, isValid);
  }, [wizard.data, sources]);

  const handleDestinationChange = (destinationId: string) => {
    wizard.setData('destinationId', destinationId);
    const destination = sources.find(s => s.id === destinationId);
    wizard.setData('destinationName', destination?.name);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>Destination Moodle Instance</InputLabel>
        <Select
          value={wizard.getData('destinationId', '')}
          onChange={(e) => handleDestinationChange(e.target.value)}
          label="Destination Moodle Instance"
        >
          {sources.map(source => (
            <MenuItem key={source.id} value={source.id}>
              <Box>
                <Typography variant="body1">{source.name}</Typography>
                {source.description && (
                  <Typography variant="caption" color="text.secondary">
                    {source.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sources.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No other Moodle instances available. You need at least two Moodle instances configured to perform a migration.
        </Alert>
      )}

      {wizard.getData('destinationId') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Selected destination: <strong>{wizard.getData('destinationName')}</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 4: Configure Migration Options
const MigrationConfigStep: React.FC = () => {
  const wizard = useWizard();
  const [categoryId, setCategoryId] = useState(wizard.getData('categoryId', '1'));
  const [includeActivities, setIncludeActivities] = useState(wizard.getData('includeActivities', true));
  const [conflictStrategy, setConflictStrategy] = useState(wizard.getData('conflictStrategy', 'skip'));

  useEffect(() => {
    wizard.setDataBulk({
      categoryId,
      includeActivities,
      conflictStrategy
    });
    wizard.setStepValidation(3, true); // This step is always valid with defaults
  }, [categoryId, includeActivities, conflictStrategy]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Migration Options
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Target Category ID</InputLabel>
        <Select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          label="Target Category ID"
        >
          <MenuItem value="1">Top Level (1)</MenuItem>
          <MenuItem value="custom">Custom Category ID</MenuItem>
        </Select>
      </FormControl>

      {categoryId === 'custom' && (
        <TextField
          fullWidth
          label="Category ID"
          type="number"
          value={wizard.getData('customCategoryId', '')}
          onChange={(e) => wizard.setData('customCategoryId', e.target.value)}
          sx={{ mb: 2 }}
          helperText="Enter the ID of the destination category"
        />
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={includeActivities}
            onChange={(e) => setIncludeActivities(e.target.checked)}
          />
        }
        label="Include CMI5 Activities"
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Conflict Strategy</InputLabel>
        <Select
          value={conflictStrategy}
          onChange={(e) => setConflictStrategy(e.target.value)}
          label="Conflict Strategy"
        >
          <MenuItem value="skip">Skip existing courses</MenuItem>
          <MenuItem value="replace">Replace existing courses</MenuItem>
          <MenuItem value="rename">Rename new courses</MenuItem>
        </Select>
      </FormControl>

      <Alert severity="info">
        <Typography variant="body2" fontWeight="bold">
          Migration Settings:
        </Typography>
        <Typography variant="body2">
          • Courses will be placed in category: {categoryId === 'custom' ? wizard.getData('customCategoryId', 'N/A') : categoryId}
        </Typography>
        <Typography variant="body2">
          • CMI5 Activities: {includeActivities ? 'Included' : 'Excluded'}
        </Typography>
        <Typography variant="body2">
          • Conflict handling: {conflictStrategy}
        </Typography>
      </Alert>
    </Box>
  );
};

// Step 5: Review & Confirm
const ReviewConfirmStep: React.FC = () => {
  const wizard = useWizard();
  const selectedCourses: MoodleCourse[] = wizard.getData('selectedCourses', []);

  useEffect(() => {
    wizard.setStepValidation(4, true);
  }, []);

  const sections: SummarySection[] = [
    {
      title: 'Migration Details',
      items: [
        { label: 'Source', value: wizard.getData('sourceName', 'Unknown'), highlight: true },
        { label: 'Destination', value: wizard.getData('destinationName', 'Unknown'), highlight: true },
        { label: 'Courses to migrate', value: selectedCourses.length, highlight: true }
      ]
    },
    {
      title: 'Configuration',
      items: [
        { label: 'Target Category', value: wizard.getData('categoryId') === 'custom' ? wizard.getData('customCategoryId') : wizard.getData('categoryId') },
        { label: 'Include Activities', value: wizard.getData('includeActivities') ? 'Yes' : 'No' },
        { label: 'Conflict Strategy', value: wizard.getData('conflictStrategy') }
      ]
    }
  ];

  const contentItems: ContentItem[] = selectedCourses.map(course => ({
    type: 'course',
    name: course.fullname,
    id: course.shortname,
    metadata: {
      'Course ID': course.id,
      'Visible': course.visible ? 'Yes' : 'No'
    }
  }));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Migration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the migration details below before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={sections} />

      <Box sx={{ mt: 2 }}>
        <ContentPreviewPanel
          title="Courses to Migrate"
          items={contentItems}
          showMetadata
        />
      </Box>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Important:
        </Typography>
        <Typography variant="body2">
          • This operation will start a background job
        </Typography>
        <Typography variant="body2">
          • You can monitor progress in the Jobs panel
        </Typography>
        <Typography variant="body2">
          • The migration may take several minutes depending on course size
        </Typography>
      </Alert>
    </Box>
  );
};

// Main Wizard Component
export const MoodleToMoodleMigrationWizard: React.FC<MoodleToMoodleMigrationWizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const handleComplete = async (data: Record<string, any>) => {
    try {
      const selectedCourses: MoodleCourse[] = data.selectedCourses || [];
      const categoryId = data.categoryId === 'custom' ? data.customCategoryId : data.categoryId;

      // Start the migration job
      const result = await moodleApi.importCoursesFromMoodleStream(
        data.destinationId,
        data.sourceId,
        selectedCourses.map(c => c.id),
        parseInt(categoryId, 10)
      );

      if (onComplete && result.jobId) {
        onComplete(result.jobId);
      }
    } catch (err: any) {
      throw new Error(`Failed to start migration: ${err.message}`);
    }
  };

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Moodle to Moodle Migration Wizard"
      steps={WIZARD_STEPS}
      onComplete={handleComplete}
      width="lg"
      persistenceKey="moodle-to-moodle-migration-wizard"
      showResumePrompt
    >
      <WizardStep stepIndex={0}>
        <SourceSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={1}>
        <ContentSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={2}>
        <DestinationSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={3}>
        <MigrationConfigStep />
      </WizardStep>
      <WizardStep stepIndex={4}>
        <ReviewConfirmStep />
      </WizardStep>
    </WizardDialog>
  );
};
